'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { resolveImageAltText } from '@/lib/utils';

const LayerDetails = ({ layers, clickedLayer }) => {
  const activeLayer = layers[clickedLayer?.id];

  return (
    <div className="my-5 mr-5 flex justify-start sm:mr-0 sm:ml-10 lg:mx-20">
      <div className="lg:w-full">
        <div className="flex flex-row">
          <Image
            className="h-7 w-7"
            src={activeLayer.layer_number_active.url}
            width="24"
            height="24"
            sizes="24px"
            alt="Open Mattress Layer"
            loading="lazy"
          />
          <span className="h5 px-1">{activeLayer.content_title}</span>
        </div>
        <div className="ml-1">
          <div className="body-s lg:body-r my-3 sm:w-64 lg:w-80">
            <span className="max-w-[100px]">{activeLayer.content}</span>
          </div>
          <Image
            src={activeLayer.thumbnail_image.url}
            alt="Mattress Layer Thumbnail"
            className="border-warm-pewter mt-5 border"
            width="220"
            height="95"
            sizes="220px"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
};

export default function LayersAccordion({ block, position }) {
  const { title, layers, uid } = block;

  const initialLayerDetailState = layers.map((_, index) => {
    if (index === 0) {
      return { id: index, clicked: true };
    } else {
      return { id: index, clicked: false };
    }
  });

  const [showLayerDetail, setShowLayerDetail] = useState(
    initialLayerDetailState
  );

  const clickedLayer = showLayerDetail.find(({ clicked }) => clicked === true);

  const toggleLayerDetail = (clickedIndex) => {
    const nextState = showLayerDetail.map((item) => {
      if (item.id === clickedIndex) {
        return { ...item, clicked: true };
      } else if (item.id !== clickedIndex) {
        return { ...item, clicked: false };
      }
      return { ...item, clicked: !item.clicked };
    });

    setShowLayerDetail(nextState);
  };

  return (
    <section id={`${uid}_${position}`} data-name="layers-accordion">
      <div
        className={`layers-accordion bg-dawn-grey-10 text-parlor-green flex flex-col items-center justify-center pt-[60px] ${layers.length == 5 ? 'pb-10 sm:pb-[60px] lg:pb-[80px]' : layers.length == 6 ? 'pb-10 sm:pb-[60px] lg:pb-[80px]' : layers.length == 7 ? 'pb-10 sm:pb-[60px] lg:pb-[90px]' : layers.length == 8 ? 'pb-10 sm:pb-[100px] lg:pb-[120px]' : 'pb-10 sm:pb-[60px] lg:pb-[30px]'}`}
      >
        <div>
          <div
            className={`w-full ${layers.length > 7 ? 'sm:pb-20 lg:pb-[90px]' : layers.length == 7 ? 'sm:pb-15 lg:pb-[70px]' : layers.length >= 6 ? 'lg:pb-10' : 'lg:pb-3'}`}
          >
            <span className="h4 sm:h2 mx-4 mb-5 block text-center">
              {title}
            </span>
          </div>
        </div>

        <div
          className={`z-0 mx-7 flex flex-col items-center sm:h-64 sm:flex-row lg:h-80 lg:flex-row`}
        >
          <div className="lg:max-w-[700px]">
            {layers.map((item, index) => {
              return (
                <div
                  className={`layers-col relative ${showLayerDetail[index].clicked ? 'active' : ''}`}
                  key={index}
                >
                  <Image
                    className={`relative mx-auto mb-2 sm:hidden lg:hidden`}
                    src={item.mobile_image.url}
                    alt={resolveImageAltText(item.mobile_image)}
                    width="584"
                    height="90"
                    sizes="584px"
                    data-layerrindex={layers.length - 1 - index}
                  />
                  <Image
                    className={`mx-auto mb-2 pb-12 max-[640px]:hidden sm:pb-0`}
                    src={item.desktop_image.url}
                    alt={resolveImageAltText(item.desktop_image)}
                    width="700"
                    height="93"
                    sizes="(min-width: 1280px) 700px, (min-width: 1024px) 485px, (min-width: 768px) 700px, (min-width: 640px) 584px, 285px"
                    data-layerrindex={layers.length - 1 - index}
                  />
                  <button
                    className="absolute top-0 left-(--left-position-mobile) z-20 h-10 w-10 cursor-pointer sm:top-1 sm:left-(--left-position-desktop) lg:top-2"
                    style={{
                      '--left-position-desktop': `${item.desktop_button_position}%`,
                      '--left-position-mobile': `${item.mobile_button_position}%`
                    }}
                    tabIndex="0"
                    role="button"
                    aria-pressed={showLayerDetail[index].clicked}
                    onClick={() => toggleLayerDetail(index)}
                    data-image-wrapper-clicked={`${showLayerDetail[index].clicked}`}
                  >
                    <div
                      className={`${showLayerDetail[index].clicked ? 'hidden' : 'block'} group`}
                    >
                      <span className="sr-only">
                        Open Mattress Layer {index}
                      </span>
                      <span className="flex">
                        <span className="animate-ping-slow bg-parlor-green absolute inline-flex h-9 w-9 rounded-full opacity-20"></span>
                        <Image
                          className="relative inline-flex h-9 w-9 hover:opacity-0"
                          src={item.layer_number_default.url}
                          width="36"
                          height="36"
                          sizes="36px"
                          alt={resolveImageAltText(item.layer_number_default)}
                          loading="lazy"
                        />
                        <Image
                          className="absolute inline-flex w-9 opacity-0 hover:opacity-100"
                          src={item.layer_number_active.url}
                          width="36"
                          height="36"
                          sizes="36px"
                          alt={resolveImageAltText(item.layer_number_active)}
                          loading="lazy"
                        />
                      </span>
                    </div>
                    <div
                      className={`${showLayerDetail[index].clicked ? 'block' : 'hidden'}`}
                    >
                      <Image
                        src={item.layer_number_active.url}
                        width="40"
                        height="40"
                        sizes="40px"
                        alt={resolveImageAltText(item.layer_number_active)}
                        loading="lazy"
                      />
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
          <div>
            <LayerDetails
              layers={layers}
              clickedLayer={clickedLayer}
              toggleLayerDetail={toggleLayerDetail}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
